import { CheckSaveProjectService } from './check-save-project.service';
import { UserActionsLoggingManager } from '@user/service/logging/user-actions-logging.manager';
import { LayerManager } from '@3d-scene/manager/layer-manager';
import { ArticlesVisibilityService } from '../components/article/service/articles-visibility.service';
import { Subject } from 'rxjs';
import { ArticleVO } from '@article/model/vo/article.vo';
import { MarkingSystemProjectCreationService } from '@configuration/wizard/service/marking-system-project-creation.service';
import { ArticleConstants } from '@article/constants/article-constants';
import { ArticleManager } from '@article/manager/article.manager';
import { Injectable } from '@angular/core';
import { ProjectModelService } from './project-model.service';
import { MiniTerminalBlocksArticleManager } from '@article/manager/mini-terminal-blocks-article.manager';
import { MiniTerminalBlocksProjectCreationService } from '@configuration/wizard/service/mini-terminal-blocks-project-creation.service';
import { McsGdsProjectCreationProcessor } from '@configuration/wizard/service/mcs-gds-project-creation.processor';
import { ProjectModel } from '../model/project.model';
import { AlertHelper } from '@shared/alert/alert.helper';
import { BaseArticleProjectProcessorFactory } from '@article/model/base-article-project-processor-factory';
import { ArticleContainerAttachedService } from '../components/article/service/article-container-attached.service';

@Injectable({ providedIn: 'root' })
export class CreateProjectService {

  constructor(
    private checkSaveProjectService: CheckSaveProjectService,
    private projectModelService: ProjectModelService,
    private layerManager: LayerManager,
    private articlesVisibilityService: ArticlesVisibilityService,
    private markingSystemProjectCreationService: MarkingSystemProjectCreationService,
    private miniTerminalBlocksArticleManager: MiniTerminalBlocksArticleManager,
    private userActionsLoggingManager: UserActionsLoggingManager,
    private miniTerminalBlocksProjectCreationService: MiniTerminalBlocksProjectCreationService,
    private projectModel: ProjectModel,
    private alertHelper: AlertHelper,
    private baseArticleProjectProcessorFactory: BaseArticleProjectProcessorFactory,
    private articleContainerAttachedService: ArticleContainerAttachedService,
  ) {}

  private _projectStarted$ = new Subject<void>();

  public get projectStarted$() {
    return this._projectStarted$.asObservable();
  }

  async startNewProject(): Promise<boolean> {
    if (await this.checkSaveProjectService.checkSaveProjectOrAbort()) {
      await this.projectModelService.clearCurrentProject();
      this.userActionsLoggingManager.log('projectCreate');
      this.layerManager.reset();
      this.articlesVisibilityService.refreshHandler();
      this._projectStarted$.next(undefined);
      return true;
    }
    return false;
  }

  async startNewProjectWithoutAlert(): Promise<void> {
    await this.projectModelService.clearCurrentProject();
    this.layerManager.reset();
    this.articlesVisibilityService.refreshHandler();
    this._projectStarted$.next(undefined);
  }

  public async createMcsProject(predeterminedArticle: ArticleVO): Promise<boolean> {
    return this.createMcsGdsProject("MCS", predeterminedArticle);
  }

  public async createGdsProject(predeterminedArticle: ArticleVO): Promise<boolean> {
    return this.createMcsGdsProject("GDS", predeterminedArticle);
  }

  public async createMcsGdsProject(projectType: "MCS" | "GDS", predeterminedArticle: ArticleVO): Promise<boolean> {

    const mcsGdsProjectCreationProcessor = new McsGdsProjectCreationProcessor(
      this.projectModel,
      this.projectModelService,
      this.alertHelper,
      this.miniTerminalBlocksArticleManager,
      this.baseArticleProjectProcessorFactory,
      this.articleContainerAttachedService,
      this.miniTerminalBlocksProjectCreationService
    );

    if (await this.startNewProject()) {
      await mcsGdsProjectCreationProcessor.assembleProject(predeterminedArticle, projectType);
      mcsGdsProjectCreationProcessor.clear();
      return true;
    }
    return false;
  }

  public async createMarkingSystemProject(predeterminedArticle: ArticleVO) {
    if (await this.startNewProject()) {
      await this.markingSystemProjectCreationService.createNewProject(predeterminedArticle);
    }
  }

  public async createFreeMounting(): Promise<void> {
    this.projectModelService.currentProjectType = "FREE_MOUNTING";
    try {
      await this.miniTerminalBlocksArticleManager.quickLoadArticle({
        data: ArticleConstants.DEFAULT_FREE_MOUNTING_ARTICLE_ID,
        quickLoadType: ArticleManager.QUICK_LOAD_TYPE_LOAD_BY_ARTICLE_ID,
      });
    } catch (error) {
      // TODO SLK Is this call correct?
      this.miniTerminalBlocksProjectCreationService.projectCreationFailureHandler();
    }
  }
}
